import { InputValuesSchema, MAX_DASHBOARD_FILTERS, type DashboardFilter, type InputValues, type Row, type SemanticQueryInput } from "@cd/core";
import type { Highlight } from "@cd/viz";
import { parseZoom, type DateZoom } from "./dateZoom";
import { parseDrill, type Drill } from "./drill";

type Value = Row[string];

/**
 * A filter added by clicking a value on a dashboard (a "chip"). Page state only: it lives in the
 * URL, never in the saved dashboard, and applies to every widget that can take it.
 */
export interface PageFilter {
  /** The data pack the field belongs to; absent for the workspace's first. */
  pack?: string;
  ref: string;
  /** The field's label, e.g. "Category". */
  label: string;
  /** Keep everything except these values, rather than only these. */
  exclude?: boolean;
  /** Raw values. An empty value (null) always gets a chip of its own: it's a different filter. */
  values: Value[];
  /** How each value is shown, in the same order. */
  valueLabels: string[];
}

const isValue = (v: unknown): v is Value =>
  v === null || typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v)) || (typeof v === "string" && v.length <= 200);
const isText = (v: unknown, max: number): v is string => typeof v === "string" && v.length <= max;

function isPageFilter(f: unknown): f is PageFilter {
  if (!f || typeof f !== "object") return false;
  const c = f as Record<string, unknown>;
  return (c.pack === undefined || (isText(c.pack, 64) && /^[a-z0-9_]+$/.test(c.pack)))
    && isText(c.ref, 200) && isText(c.label, 120)
    && (c.exclude === undefined || typeof c.exclude === "boolean")
    && Array.isArray(c.values) && c.values.length > 0 && c.values.length <= 100 && c.values.every(isValue)
    && Array.isArray(c.valueLabels) && c.valueLabels.length === c.values.length && c.valueLabels.every((l) => isText(l, 200));
}

/**
 * The dashboard route's search params: chips (`f`), a date zoom (`z`), a breakdown (`d`) and a
 * viewer's own input values (`in`). A hand-edited or stale one is dropped, never an error.
 */
export function parseDashboardSearch(search: Record<string, unknown>): { f?: PageFilter[]; z?: DateZoom; d?: Drill; in?: InputValues } {
  const f = search.f;
  const z = parseZoom(search.z);
  const d = parseDrill(search.d);
  const inputs = InputValuesSchema.safeParse(search.in);
  return {
    ...(Array.isArray(f) && f.length && f.length <= MAX_DASHBOARD_FILTERS && f.every(isPageFilter) ? { f } : {}),
    ...(z ? { z } : {}),
    ...(d ? { d } : {}),
    ...(inputs.success && Object.keys(inputs.data).length ? { in: inputs.data } : {}),
  };
}

const isEmptyValue = (f: Pick<PageFilter, "values">) => f.values[0] === null;
const sameChip = (a: PageFilter, b: Omit<PageFilter, "values" | "valueLabels"> & { values: Value[] }) =>
  (a.pack ?? "") === (b.pack ?? "") && a.ref === b.ref && !!a.exclude === !!b.exclude && isEmptyValue(a) === isEmptyValue(b);

/** Adds one clicked value: into the chip for the same field and kind if there is one, else as a new chip. */
export function addPageFilter(
  chips: PageFilter[], pick: { pack?: string; ref: string; label: string; exclude?: boolean; value: Value; valueLabel: string },
): PageFilter[] {
  const { value, valueLabel, ...field } = pick;
  const next = { ...field, values: [value], valueLabels: [valueLabel] };
  const i = chips.findIndex((c) => sameChip(c, next));
  if (i < 0) return chips.length >= MAX_DASHBOARD_FILTERS ? chips : [...chips, next];
  const chip = chips[i]!;
  if (chip.values.includes(value)) return chips;
  return chips.map((c, j) => (j === i ? { ...c, values: [...c.values, value], valueLabels: [...c.valueLabels, valueLabel] } : c));
}

/** What the worker gets: one query filter per chip. */
export function toDashboardFilters(chips: PageFilter[]): DashboardFilter[] {
  return chips.map((c) =>
    isEmptyValue(c)
      ? { pack: c.pack, ref: c.ref, op: c.exclude ? "not_null" : "is_null", values: [] }
      : { pack: c.pack, ref: c.ref, op: c.exclude ? "not_in" : "in", values: c.values as (string | number | boolean)[] },
  );
}

/** "Category is Cocktails", "Server is any of Ana, Bo", "Channel is not Takeout". */
export function describePageFilter(c: PageFilter): string {
  if (isEmptyValue(c)) return `${c.label} ${c.exclude ? "is not empty" : "is empty"}`;
  const many = c.values.length > 1;
  const verb = c.exclude ? (many ? "is none of" : "is not") : many ? "is any of" : "is";
  return `${c.label} ${verb} ${c.valueLabels.join(", ")}`;
}

/**
 * Which chips a query is sent, and which it highlights instead. A chip on a field the query is
 * grouped by highlights its values rather than filtering, or "Sales by category" would shrink to one
 * bar. Exclusions still filter, and a query pinned to its own values of the field gets the chip so
 * the server can skip it and say why.
 */
export function chipsFor(query: SemanticQueryInput, chips: PageFilter[]): { sent: PageFilter[]; highlight?: Highlight[] } {
  const grouped = new Set((query.dimensions ?? []).filter((d) => !d.grain).map((d) => d.ref));
  const pinned = new Set((query.filters ?? []).filter((f) => ["eq", "in", "is_null"].includes(f.op)).map((f) => f.ref));
  const shown = chips.filter((c) =>
    !c.exclude && grouped.has(c.ref) && !pinned.has(c.ref) && (c.pack ?? "") === (query.pack ?? ""));
  return {
    sent: chips.filter((c) => !shown.includes(c)),
    highlight: shown.length ? shown.map((c): Highlight => ({ key: c.ref, values: c.values })) : undefined,
  };
}
